import React, { useState } from "react";
import { BaseImportExportModalComponent } from "../../../components/modals";
import { DataTypes } from "../../../shared";
import { getRegionFromURL } from "../../../shared/utils/region";
import {
  getOpeningBalancesSpecificDescription,
  getTimeFeesDisbursementsRegionSpecificLabel,
  PRODUCT_TIERS,
  getInvoicesBasicSpecificDescription,
  getInvoicesDetailedSpecificDescription,
} from "../../../shared/utils/constants";
import log from "../../../shared/utils/log";
import {
  isAnyInvoiceOption,
  isBasicInvoiceOption,
  isDetailedInvoiceOption,
} from "../../../shared/utils/invoiceOptionUtils";

export interface ImportOption {
  label: string;
  description?: string;
  selected: boolean;
  dataTypes: string[];
  disabled?: boolean;
}

export const ImportTypeSelectionModal: React.FC<{
  closeModal: () => void;
  onConfirm: (selectedOptions: string[]) => void;
  productId: string | undefined;
  titleText: string;
  subtitleText: string;
  existingDataTypes: string[];
}> = ({
  closeModal,
  onConfirm,
  productId,
  titleText,
  subtitleText,
  existingDataTypes,
}) => {
  const region = getRegionFromURL();
  const [dataTypesToShow, setDataTypesToShow] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const isGrowOrProsper =
    productId === PRODUCT_TIERS.GROW || productId === PRODUCT_TIERS.PROSPER;

  // Basic vs Detailed Helpers
  const isBasicOption = (opt: ImportOption) =>
    isBasicInvoiceOption(opt.dataTypes);

  const isDetailedOption = (opt: ImportOption) =>
    isDetailedInvoiceOption(opt.dataTypes);

  // Initial Options
  const [options, setOptions] = useState<ImportOption[]>(() => {
    // Initialise options with region and tier logic
    let initialOptions: ImportOption[] = [
      {
        label: DataTypes.Staff,
        selected: false,
        dataTypes: [DataTypes.Staff],
      },
      {
        label: DataTypes.Contacts,
        selected: false,
        dataTypes: [DataTypes.Contacts],
      },
      {
        label: DataTypes.Matters,
        selected: false,
        dataTypes: [DataTypes.Matters, DataTypes.MatterTypes],
      },
      ...(isGrowOrProsper
        ? [
            {
              label: DataTypes.MatterTags,
              selected: false,
              dataTypes: [DataTypes.MatterTags],
            },
            {
              label: "Matter Roles",
              selected: false,
              dataTypes: [DataTypes.MatterRelationships],
            },
            {
              label: DataTypes.MatterCriticalDates,
              selected: false,
              dataTypes: [DataTypes.MatterCriticalDates],
            },
            {
              label: DataTypes.MatterArchiveDetails,
              selected: false,
              dataTypes: [DataTypes.MatterArchiveDetails],
            },
          ]
        : []),
      {
        label: DataTypes.OpeningBalances,
        description: getOpeningBalancesSpecificDescription(region),
        selected: false,
        dataTypes: [DataTypes.OpeningBalances],
      },
      {
        label: getTimeFeesDisbursementsRegionSpecificLabel(region),
        description: "Includes Activity Types",
        selected: false,
        dataTypes: [
          DataTypes.ActivityTypes,
          DataTypes.UnbilledTimeAndFees,
          DataTypes.UnbilledDisbursements,
        ],
      },

      // Invoices: Basic / Detailed
      {
        label: "Invoices - Basic",
        description: getInvoicesBasicSpecificDescription(region),
        selected: false,
        dataTypes: [DataTypes.Invoices],
      },
      {
        label: "Invoices - Detailed",
        description: getInvoicesDetailedSpecificDescription(region),
        selected: false,
        dataTypes: [DataTypes.Invoices, DataTypes.InvoiceItems],
      },
    ];

    if (
      productId === PRODUCT_TIERS.BOOST ||
      productId === PRODUCT_TIERS.GROW ||
      productId === PRODUCT_TIERS.PROSPER
    ) {
      initialOptions.push({
        label: DataTypes.Memos,
        selected: false,
        dataTypes: [DataTypes.Memos],
      });
      initialOptions.push({
        label: DataTypes.Tasks,
        selected: false,
        dataTypes: [DataTypes.Tasks],
      });
      initialOptions.push({
        label: "Calendar Events",
        selected: false,
        dataTypes: [DataTypes.Events],
      });
    }

    // Add Safe Custody Packets and Documents only for Grow/Prosper tiers and non-US regions
    if (isGrowOrProsper && region !== "US") {
      initialOptions.push({
        label: "Safe Custody Packets and Documents",
        selected: false,
        dataTypes: [
          DataTypes.SafeCustodyPackets,
          DataTypes.SafeCustodyDocuments,
        ],
      });
    }

    // Checking to see if modal is being opened from Add Items button or initial import
    const isAddItemsMode = existingDataTypes.length > 0;

    // Filter out existing import types, including grouped ones
    // Special check required for invoices and invoice items
    const filtered = initialOptions.filter((option) => {
      const optionContainsInvoice = isAnyInvoiceOption(option.dataTypes);
      const invoicesAlreadyChosen = isAnyInvoiceOption(existingDataTypes);

      // Only hide invoice options when:
      // 1) we are inside the ADD ITEMS modal from workflow (not initial import)
      // 2) and invoices were already chosen
      if (isAddItemsMode && optionContainsInvoice && invoicesAlreadyChosen) {
        return false;
      }

      // Check if any of the option's `importTypes` are in the existingDataTypes
      return !option.dataTypes.every((dataType) =>
        existingDataTypes.includes(dataType),
      );
    });

    return filtered;
  });

  // Mutually exclusive invoice logic
  const handleOptionChange = (index: number) => {
    const newOptions = [...options];
    newOptions[index].selected = !newOptions[index].selected;

    const selectedOption = newOptions[index];

    // Handle mutual exclusivity for invoice options
    if (isBasicOption(selectedOption) || isDetailedOption(selectedOption)) {
      newOptions.forEach((opt) => {
        if (isBasicOption(selectedOption)) {
          if (isDetailedOption(opt)) {
            opt.disabled = selectedOption.selected;
            if (selectedOption.selected) opt.selected = false;
          } else {
            opt.disabled = false;
          }
        }

        if (isDetailedOption(selectedOption)) {
          if (isBasicOption(opt)) {
            opt.disabled = selectedOption.selected;
            if (selectedOption.selected) opt.selected = false;
          } else {
            opt.disabled = false;
          }
        }
      });
    }

    // Handle dataTypesToShow updates
    if (selectedOption.selected) {
      // If Basic is selected, always remove InvoiceItems
      if (isBasicOption(selectedOption)) {
        setDataTypesToShow((prev) =>
          prev.filter((t) => t !== DataTypes.InvoiceItems),
        );
      }

      // If the option is selected, add its cards to the list
      setDataTypesToShow((prevDataTypes) => {
        const newDataTypes = [...prevDataTypes];
        selectedOption.dataTypes.forEach((dataType) => {
          if (!newDataTypes.includes(dataType)) {
            newDataTypes.push(dataType); // Add the card if it's not already in the list
          }
        });
        return newDataTypes;
      });
    } else {
      // If detailed invoices was deselected remove Invoice and InvoiceItems
      if (isDetailedOption(selectedOption)) {
        setDataTypesToShow((prev) =>
          prev.filter(
            (t) => t !== DataTypes.InvoiceItems && t !== DataTypes.Invoices,
          ),
        );
      }

      // If Basic was deselected remove Invoices
      if (
        isBasicOption(selectedOption) &&
        !newOptions.some((o) => o.selected && isDetailedOption(o))
      ) {
        setDataTypesToShow((prev) =>
          prev.filter((t) => t !== DataTypes.Invoices),
        );
      }

      // If any other option is deselected, check if any other selected options require this option's import types
      setDataTypesToShow((prevDataTypes) => {
        const newDataTypes = prevDataTypes.filter(
          (dataType) =>
            !selectedOption.dataTypes.includes(dataType) ||
            // Keep import types that are still required by other selected options
            newOptions.some(
              (option) =>
                option.selected && option.dataTypes.includes(dataType),
            ),
        );
        return newDataTypes;
      });
    }
    setOptions(newOptions);
  };

  const handleUnselectAll = () => {
    setOptions(options.map((option) => ({ ...option, selected: false })));
    setDataTypesToShow([]); // Clear import types when unselecting all
  };

  // Select all options
  const handleSelectAll = () => {
    // Detect previous invoice choice with a fall back to existingDataTypes since in
    // Add Items mode, already-chosen invoice options are filtered out of options.
    const previouslySelectedBasic =
      options.some((o) => o.selected && isBasicOption(o)) ||
      isBasicInvoiceOption(existingDataTypes);
    const previouslySelectedDetailed =
      options.some((o) => o.selected && isDetailedOption(o)) ||
      isDetailedInvoiceOption(existingDataTypes);

    // Decide which one to keep
    let selectBasic = false;
    let selectDetailed = false;

    if (previouslySelectedBasic) selectBasic = true;
    else if (previouslySelectedDetailed) selectDetailed = true;
    else selectDetailed = true; // default if none picked

    // Always show these types
    const defaultDataTypes = [
      DataTypes.Staff,
      DataTypes.Contacts,
      DataTypes.Matters,
      DataTypes.MatterTypes,
      ...(isGrowOrProsper
        ? [
            DataTypes.MatterTags,
            DataTypes.MatterRelationships,
            DataTypes.MatterCriticalDates,
            DataTypes.MatterArchiveDetails,
          ]
        : []),
      DataTypes.OpeningBalances,
      DataTypes.ActivityTypes,
      DataTypes.UnbilledTimeAndFees,
      DataTypes.UnbilledDisbursements,
    ];

    const dataTypesToShow = [...defaultDataTypes];

    // Apply invoice selection memory
    if (selectBasic) {
      dataTypesToShow.push(DataTypes.Invoices);
    }

    if (selectDetailed) {
      dataTypesToShow.push(DataTypes.Invoices, DataTypes.InvoiceItems);
    }

    // Add region and tier specific imports
    if (
      productId === PRODUCT_TIERS.BOOST ||
      productId === PRODUCT_TIERS.GROW ||
      productId === PRODUCT_TIERS.PROSPER
    ) {
      dataTypesToShow.push(DataTypes.Memos, DataTypes.Tasks, DataTypes.Events);
    }

    if (isGrowOrProsper && region !== "US") {
      dataTypesToShow.push(
        DataTypes.SafeCustodyPackets,
        DataTypes.SafeCustodyDocuments,
      );
    }

    setDataTypesToShow(dataTypesToShow);

    // Update option selections to match invoice mode
    const updatedOptions = options.map((opt) => {
      if (isBasicOption(opt)) {
        return { ...opt, selected: selectBasic, disabled: !selectBasic };
      }
      if (isDetailedOption(opt)) {
        return { ...opt, selected: selectDetailed, disabled: !selectDetailed };
      }
      return { ...opt, selected: true, disabled: false };
    });

    setOptions(updatedOptions);
  };

  const handleProceed = async () => {
    setIsLoading(true);
    try {
      // Deduplicate the import types to ensure no duplicates
      const uniqueDataTypes = dataTypesToShow.filter((type, index, self) => {
        return self.indexOf(type) === index;
      });
      log("Final unique import types: ", uniqueDataTypes);

      onConfirm(uniqueDataTypes); // Pass deduplicated string array to parent
      closeModal(); // Close the modal after proceeding
    } catch (error) {
      console.error("Error during import confirmation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseImportExportModalComponent
      titleText={titleText}
      subtitleText={subtitleText}
      closeModal={closeModal}
      isLoading={isLoading}
      options={options}
      onToggleOption={handleOptionChange}
      onSelectAll={handleSelectAll}
      onUnselectAll={handleUnselectAll}
      onProceed={handleProceed}
    />
  );
};
